import { Calendar, X, Filter, Check } from 'lucide-react'
import React, { ReactNode } from 'react'

import { CatalogSelect } from './CatalogSelect'
import { SearchFilter } from './SearchFilter'
import { Button } from './button'
import { Checkbox } from './checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select'

export type FilterType =
  | 'search'
  | 'branch'
  | 'status'
  | 'seller'
  | 'delivery'
  | 'payment'
  | 'invoice'
  | 'saleType'
  | 'dates'
  | 'bank'
  | 'loginType'
  | 'successResult'
  | 'eventType'
  | 'moduleType'
  | 'allBranches'
  | 'product'
  | 'movementType'
  | 'transferType'
  | 'originBranch'
  | 'destinationBranch'
export type DateShortcut = 'today' | 'yesterday' | '1week' | '1month' | '1year' | '5years'

export interface FilterValues {
  search?: string
  branch?: string
  status?: string
  seller?: string
  delivery?: string
  payment?: string
  bank?: string
  invoice?: string
  saleType?: string
  startDate?: string
  endDate?: string
  loginType?: string
  successResult?: string
  eventType?: string
  moduleType?: string
  allBranches?: boolean
  product?: string
  movementType?: string
  transferType?: string
  originBranch?: string
  destinationBranch?: string
  paymentStatus?: string
}

export interface GlobalFilterBarProps {
  /** Array con los nombres de los filtros que se quieren mostrar */
  show?: FilterType[]
  
  /** 
   * Array con los atajos de fecha rápidos que se quieren mostrar.
   * Si no se provee y 'dates' está en show, se muestran todos por defecto.
   */
  dateShortcuts?: DateShortcut[]

  /** Objeto con los valores actuales de los filtros */
  values?: FilterValues

  /** Callback que se ejecuta cuando CUALQUIER filtro cambia */
  onChange?: (changes: Partial<FilterValues>) => void

  /** Opciones personalizadas para los filtros (ej. opciones de 'status', placeholders) */
  customOptions?: {
    searchPlaceholder?: string
    statusOptions?: { value: string; label: string }[]
    statusPlaceholder?: string
    banksOptions?: string[]
    moduleOptions?: { value: string; label: string }[]
    productPlaceholder?: string
    movementTypePlaceholder?: string
    movementTypeOptions?: { value: string; label: string }[]
    transferTypePlaceholder?: string
    transferTypeOptions?: { value: string; label: string }[]
    originBranchPlaceholder?: string
    destinationBranchPlaceholder?: string
  }

  /** Función para limpiar todos los filtros (si hay filtros activos) */
  onClearFilters?: () => void
  hasActiveFilters?: boolean

  /** Filtros extra inyectados manualmente */
  children?: ReactNode
}

export function GlobalFilterBar({
  show = [],
  dateShortcuts = ['today', 'yesterday', '1week', '1month', '1year', '5years'],
  values = {},
  onChange,
  customOptions = {},
  onClearFilters,
  hasActiveFilters = false,
  children
}: GlobalFilterBarProps) {
  
  // Helpers para revisar si un filtro debe mostrarse
  const isVisible = (filter: FilterType) => show.includes(filter)
  const isShortcutVisible = (shortcut: DateShortcut) => dateShortcuts.includes(shortcut)

  // Helper de cambio genérico
  const handleChange = (key: keyof FilterValues, value: string) => {
    if (onChange) onChange({ [key]: value })
  }

  const getISODate = (d: Date) => {
    const offset = d.getTimezoneOffset() * 60000;
    const localDate = new Date(d.getTime() - offset);
    return localDate.toISOString().split('T')[0];
  }

  const setQuickDate = (kind: DateShortcut) => {
    if (!onChange) return
    
    const endD = new Date()
    const startD = new Date()

    switch (kind) {
      case 'today': 
        break;
      case 'yesterday':
        startD.setDate(startD.getDate() - 1)
        endD.setDate(endD.getDate() - 1)
        break;
      case '1week':
        startD.setDate(startD.getDate() - 7)
        break;
      case '1month':
        startD.setMonth(startD.getMonth() - 1)
        break;
      case '1year':
        startD.setFullYear(startD.getFullYear() - 1)
        break;
      case '5years':
        startD.setFullYear(startD.getFullYear() - 5)
        break;
    }
    
    onChange({ 
      startDate: getISODate(startD), 
      endDate: getISODate(endD) 
    })
  }

  const handleClearDates = () => {
    if (onChange) onChange({ startDate: '', endDate: '' })
  }

  const todayStr = getISODate(new Date())
  const yesterdayD = new Date()
  yesterdayD.setDate(yesterdayD.getDate() - 1)
  const yesterdayStr = getISODate(yesterdayD)

  return (
    <div className="rounded-lg border border-border bg-card text-card-foreground p-4 shadow-sm">
      
      {/* Contenedor de Filtros */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        
        {isVisible('search') && (
          <SearchFilter
            initialValue={values.search || ''}
            placeholder={customOptions.searchPlaceholder || 'Buscar...'}
            onSearch={(val: string) => handleChange('search', val)}
          />
        )}

        {isVisible('product') && (
          <CatalogSelect
            catalog="products"
            value={values.product || ''}
            onChange={(val) => handleChange('product', val || '')}
            placeholder={customOptions.productPlaceholder || 'Todos los productos'}
          />
        )}

        {isVisible('transferType') && (
          <Select value={values.transferType || 'all'} onValueChange={(val) => handleChange('transferType', val === 'all' ? '' : val)}>
            <SelectTrigger>
              <SelectValue placeholder={customOptions.transferTypePlaceholder || 'Todos los tipos'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {(customOptions.transferTypeOptions || []).map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {isVisible('originBranch') && (
          <CatalogSelect
            catalog="sucursales"
            value={values.originBranch || ''}
            onChange={(val) => handleChange('originBranch', val || '')}
            placeholder={customOptions.originBranchPlaceholder || 'Sucursal Origen'}
          />
        )}

        {isVisible('destinationBranch') && (
          <CatalogSelect
            catalog="sucursales"
            value={values.destinationBranch || ''}
            onChange={(val) => handleChange('destinationBranch', val || '')}
            placeholder={customOptions.destinationBranchPlaceholder || 'Sucursal Destino'}
          />
        )}

        {isVisible('movementType') && (
          <Select value={values.movementType || 'all'} onValueChange={(val) => handleChange('movementType', val === 'all' ? '' : val)}>
            <SelectTrigger>
              <SelectValue placeholder={customOptions.movementTypePlaceholder || 'Todos los tipos'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {(customOptions.movementTypeOptions || []).map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {isVisible('loginType') && (
          <Select value={values.loginType || 'all'} onValueChange={(val) => handleChange('loginType', val === 'all' ? '' : val)}>
            <SelectTrigger>
              <SelectValue placeholder="Todos los tipos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="pin">PIN</SelectItem>
              <SelectItem value="password">Contraseña</SelectItem>
            </SelectContent>
          </Select>
        )}

        {isVisible('successResult') && (
          <Select value={values.successResult || 'all'} onValueChange={(val) => handleChange('successResult', val === 'all' ? '' : val)}>
            <SelectTrigger>
              <SelectValue placeholder="Todos los resultados" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los resultados</SelectItem>
              <SelectItem value="true">Exitosos</SelectItem>
              <SelectItem value="false">Fallidos</SelectItem>
            </SelectContent>
          </Select>
        )}

        {isVisible('eventType') && (
          <Select value={values.eventType || 'all'} onValueChange={(val) => handleChange('eventType', val === 'all' ? '' : val)}>
            <SelectTrigger>
              <SelectValue placeholder="Todos los eventos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los eventos</SelectItem>
              <SelectItem value="created">Creación</SelectItem>
              <SelectItem value="updated">Edición</SelectItem>
              <SelectItem value="deleted">Eliminación</SelectItem>
              <SelectItem value="role_permissions_synced">Permisos</SelectItem>
              <SelectItem value="user_sucursals_synced">Sucursales</SelectItem>
              <SelectItem value="status_changed">Cambio de estado</SelectItem>
            </SelectContent>
          </Select>
        )}

        {isVisible('moduleType') && (
          <Select value={values.moduleType || 'all'} onValueChange={(val) => handleChange('moduleType', val === 'all' ? '' : val)}>
            <SelectTrigger>
              <SelectValue placeholder="Todos los módulos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los módulos</SelectItem>
              {(customOptions.moduleOptions || []).map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {isVisible('allBranches') && (
          <label
            className="flex h-9 cursor-pointer items-center justify-between rounded-md border border-border px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary data-[state=checked]:border-primary/30"
            data-state={values.allBranches ? 'checked' : 'unchecked'}
          >
            <div className="flex items-center gap-2">
              <Checkbox
                checked={values.allBranches || false}
                onCheckedChange={(checked) => { if(onChange) onChange({ allBranches: checked === true }) }}
                className="pointer-events-none"
              />
              Todas las sucursales
            </div>
            {values.allBranches && <Check className="h-4 w-4 text-primary" />}
          </label>
        )}

        {isVisible('branch') && (
          <CatalogSelect
            catalog="sucursales"
            value={values.branch || ''}
            onChange={(val) => handleChange('branch', val || '')}
            placeholder="Sucursal"
          />
        )}

        {isVisible('status') && (
          <Select value={values.status || 'all'} onValueChange={(val) => handleChange('status', val)}>
            <SelectTrigger>
              <SelectValue placeholder={customOptions.statusPlaceholder || 'Estado'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {(customOptions.statusOptions || []).map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {isVisible('seller') && (
          <CatalogSelect
            catalog="users?role=seller"
            value={values.seller || ''}
            onChange={(val) => handleChange('seller', val || '')}
            placeholder="Vendedor"
          />
        )}

        {isVisible('delivery') && (
          <CatalogSelect
            catalog="pos/delivery-users"
            value={values.delivery || ''}
            onChange={(val) => handleChange('delivery', val || '')}
            placeholder="Repartidor"
          />
        )}

        {isVisible('payment') && (
          <CatalogSelect
            catalog="payment-methods"
            value={values.payment || 'all'}
            onChange={(val) => handleChange('payment', val || 'all')}
            placeholder="Método de pago"
          />
        )}

        {isVisible('bank') && (
          <Select value={values.bank || 'all'} onValueChange={(val) => handleChange('bank', val)}>
            <SelectTrigger>
              <SelectValue placeholder="Banco" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los bancos</SelectItem>
              {(customOptions.banksOptions || []).map((bank) => (
                <SelectItem key={bank} value={bank}>{bank}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {isVisible('invoice') && (
          <Select value={values.invoice || 'all'} onValueChange={(val) => handleChange('invoice', val)}>
            <SelectTrigger>
              <SelectValue placeholder="Estado de facturación" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados de facturación</SelectItem>
              <SelectItem value="invoiced">Facturadas</SelectItem>
              <SelectItem value="not_invoiced">Sin facturar</SelectItem>
            </SelectContent>
          </Select>
        )}

        {isVisible('saleType') && (
          <Select value={values.saleType || 'all'} onValueChange={(val) => handleChange('saleType', val)}>
            <SelectTrigger>
              <SelectValue placeholder="Tipo de venta" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos de venta</SelectItem>
              <SelectItem value="recharge">Recarga</SelectItem>
              <SelectItem value="service">Servicio</SelectItem>
              <SelectItem value="common">Venta común</SelectItem>
            </SelectContent>
          </Select>
        )}

        {/* Filtros dinámicos adicionales */}
        {children}

      </div>
      
      {/* Sección opcional de Fechas */}
      {isVisible('dates') && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-4 xl:grid-cols-6 pt-3 border-t border-border mt-3">
          <div className="flex min-w-0 items-center gap-1.5 lg:col-span-1 xl:col-span-1">
            <Calendar className="h-4 w-4 shrink-0 text-current" />
            <span className="text-sm text-current">Desde:</span>
            <input
              type="date"
              aria-label="Fecha inicio"
              value={values.startDate || ''}
              max={values.endDate || todayStr}
              onChange={(e) => handleChange('startDate', e.target.value)}
              className="h-10 min-w-0 flex-1 rounded-md border border-input bg-transparent px-2 text-sm text-inherit focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="flex min-w-0 items-center gap-1.5 lg:col-span-1 xl:col-span-1">
            <span className="text-sm text-current">Hasta:</span>
            <input
              type="date"
              aria-label="Fecha fin"
              value={values.endDate || ''}
              min={values.startDate || ''}
              max={todayStr}
              onChange={(e) => handleChange('endDate', e.target.value)}
              className="h-10 min-w-0 flex-1 rounded-md border border-input bg-transparent px-2 text-sm text-inherit focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 md:col-span-2 xl:col-span-2 xl:justify-end">
            {isShortcutVisible('today') && (
              <button type="button" onClick={() => setQuickDate('today')} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${(values.startDate === todayStr && values.endDate === todayStr) ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}>Hoy</button>
            )}
            
            {isShortcutVisible('yesterday') && (
              <button type="button" onClick={() => setQuickDate('yesterday')} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${(values.startDate === yesterdayStr && values.endDate === yesterdayStr) ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}>Ayer</button>
            )}
            
            {isShortcutVisible('1week') && (
              <button type="button" onClick={() => setQuickDate('1week')} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${(values.startDate === getISODate(new Date(new Date().setDate(new Date().getDate() - 7))) && values.endDate === todayStr) ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}>Hace 1 Sem</button>
            )}
            
            {isShortcutVisible('1month') && (
              <button type="button" onClick={() => setQuickDate('1month')} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${(values.startDate === getISODate(new Date(new Date().setMonth(new Date().getMonth() - 1))) && values.endDate === todayStr) ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}>Hace 1 Mes</button>
            )}
            
            {isShortcutVisible('1year') && (
              <button type="button" onClick={() => setQuickDate('1year')} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${(values.startDate === getISODate(new Date(new Date().setFullYear(new Date().getFullYear() - 1))) && values.endDate === todayStr) ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}>Hace 1 Año</button>
            )}
            
            {isShortcutVisible('5years') && (
              <button type="button" onClick={() => setQuickDate('5years')} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${(values.startDate === getISODate(new Date(new Date().setFullYear(new Date().getFullYear() - 5))) && values.endDate === todayStr) ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}>Hace 5 Años</button>
            )}
            
            {(values.startDate || values.endDate) && (
              <button type="button" onClick={handleClearDates} className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground/70 hover:bg-accent hover:text-foreground transition-colors">
                <X className="mr-1 inline h-3 w-3" />Limpiar fechas
              </button>
            )}
            
            {hasActiveFilters && onClearFilters && (
              <Button type="button" variant="outline" size="sm" onClick={onClearFilters} className="ml-2 h-7 rounded-full text-xs">
                <Filter className="mr-1 h-3 w-3" /> Limpiar
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
